import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, ShieldAlert, FileText, Save, Trash2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import cultivarService from '../../services/cultivar';
import {
  CULTIVAR_CONTRACT_ROLES,
  CULTIVAR_CONTRACT_ROLE_LABELS,
  type CultivarContractRole,
  type CultivarProgram,
  type CultivarProgramContractTemplate,
} from '../../types/cultivar';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import contractService from '../../services/contracts';
import { CONTRACT_TIER_LABELS, type ContractFamily } from '../../types/contracts';
import { ContractHistory, Fingerprint } from '../../components/cultivar/contracts/ContractBits';

interface RowState {
  family_id: number | '';
  required: boolean;
  notes: string;
  saving: boolean;
  removing: boolean;
}

function emptyRow(): RowState {
  return { family_id: '', required: true, notes: '', saving: false, removing: false };
}

function ProgramContractTemplates() {
  const { id } = useParams<{ id: string }>();
  const programId = id ? parseInt(id, 10) : NaN;
  const navigate = useNavigate();
  const { hasAccess: canAdmin, loading: licenseLoading } = useModuleLicense('cultivar', 'admin');

  const [program, setProgram] = useState<CultivarProgram | null>(null);
  const [assignments, setAssignments] = useState<Record<CultivarContractRole, CultivarProgramContractTemplate | null>>({
    sublicensee_nursery: null,
    end_customer_grow: null,
    end_customer_non_prop: null,
    end_customer_royalty: null,
    marketing_approval: null,
  });
  const [families, setFamilies] = useState<ContractFamily[]>([]);
  const [historyKey, setHistoryKey] = useState(0);
  const [rows, setRows] = useState<Record<CultivarContractRole, RowState>>({
    sublicensee_nursery: emptyRow(),
    end_customer_grow: emptyRow(),
    end_customer_non_prop: emptyRow(),
    end_customer_royalty: emptyRow(),
    marketing_approval: emptyRow(),
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!Number.isFinite(programId)) return;
    setLoading(true);
    setError(null);
    try {
      const [progResp, assignResp, tplResp] = await Promise.all([
        cultivarService.getProgram(programId),
        cultivarService.listContractTemplates(programId),
        contractService.list(),
      ]);
      if (progResp.success && progResp.data) setProgram(progResp.data as CultivarProgram);
      if (tplResp.success) setFamilies(tplResp.data ?? []);
      const nextAssign: Record<CultivarContractRole, CultivarProgramContractTemplate | null> = {
        sublicensee_nursery: null,
        end_customer_grow: null,
        end_customer_non_prop: null,
        end_customer_royalty: null,
        marketing_approval: null,
      };
      const nextRows: Record<CultivarContractRole, RowState> = {
        sublicensee_nursery: emptyRow(),
        end_customer_grow: emptyRow(),
        end_customer_non_prop: emptyRow(),
        end_customer_royalty: emptyRow(),
        marketing_approval: emptyRow(),
      };
      if (assignResp.success && assignResp.data) {
        for (const row of assignResp.data as CultivarProgramContractTemplate[]) {
          nextAssign[row.role] = row;
          nextRows[row.role] = {
            family_id: row.family_id,
            required: row.required,
            notes: row.notes || '',
            saving: false,
            removing: false,
          };
        }
      }
      setAssignments(nextAssign);
      setRows(nextRows);
      setHistoryKey((k) => k + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load contract templates');
    } finally {
      setLoading(false);
    }
  }, [programId]);

  useEffect(() => { load(); }, [load]);

  const updateRow = (role: CultivarContractRole, patch: Partial<RowState>) => {
    setRows((prev) => ({ ...prev, [role]: { ...prev[role], ...patch } }));
  };

  const save = async (role: CultivarContractRole) => {
    const row = rows[role];
    if (!row.family_id) {
      setError('Choose a contract before saving.');
      return;
    }
    updateRow(role, { saving: true });
    setError(null);
    setInfo(null);
    try {
      const resp = await cultivarService.upsertContractTemplate(programId, role, {
        family_id: row.family_id,
        required: row.required,
        notes: row.notes || null,
      });
      if (!resp.success) throw new Error(resp.error?.message || 'Save failed');
      setInfo(`${CULTIVAR_CONTRACT_ROLE_LABELS[role]} saved.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      updateRow(role, { saving: false });
    }
  };

  const remove = async (role: CultivarContractRole) => {
    updateRow(role, { removing: true });
    setError(null);
    setInfo(null);
    try {
      const resp = await cultivarService.removeContractTemplate(programId, role);
      if (!resp.success) throw new Error(resp.error?.message || 'Remove failed');
      setInfo(`${CULTIVAR_CONTRACT_ROLE_LABELS[role]} removed.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Remove failed');
    } finally {
      updateRow(role, { removing: false });
    }
  };

  if (licenseLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!canAdmin) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card>
          <CardContent className="p-6 flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-semibold mb-1">Cultivar admin seat required</h3>
              <p className="text-sm text-gray-600">Assigning contract templates is restricted to users with a cultivar admin licence seat.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-gray-700">{error || 'Program not found.'}</p>
            <Button variant="secondary" onClick={() => navigate('/cultivar/admin/programs')} className="mt-3">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to programs
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={`Contracts — ${program.name}`}
        subtitle="The contract each role signs at order time. Each required role sends one Adobe Sign envelope to the customer."
        icon={<FileText className="h-6 w-6 text-white" />}
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        <p className="text-sm text-gray-600">
          A role names a <Link to="/cultivar/contracts" className="text-primary-600 hover:underline">contract</Link>,
          not a version. Every order is sent the version in force when it is dispatched, and publishing a
          new version cancels and re-sends any still waiting for a signature. Changes here are recorded and
          emailed to the cultivar admins.
        </p>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => navigate('/cultivar/admin/programs')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Programs
          </Button>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded px-4 py-2 text-sm">{error}</div>}
        {info && <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-4 py-2 text-sm">{info}</div>}

        <div className="space-y-3">
          {CULTIVAR_CONTRACT_ROLES.map((role) => {
            const assignment = assignments[role];
            const row = rows[role];
            return (
              <Card key={role}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <h3 className="font-semibold">{CULTIVAR_CONTRACT_ROLE_LABELS[role]}</h3>
                      <p className="text-xs text-gray-500 font-mono mt-0.5">{role}</p>
                    </div>
                    {assignment && (
                      assignment.current_version ? (
                        <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-right">
                          {assignment.family_name} · v{assignment.current_version} in force
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-right">
                          {assignment.family_name} · not published — orders cannot be sent
                        </span>
                      )
                    )}
                  </div>
                  {assignment?.current_content_hash && (
                    <div className="mb-2"><Fingerprint hash={assignment.current_content_hash} /></div>
                  )}
                  <div className="grid grid-cols-12 gap-3 items-end">
                    <label className="text-sm col-span-12 md:col-span-5">
                      <span className="text-gray-700">Contract</span>
                      <select value={row.family_id}
                        onChange={(e) => updateRow(role, { family_id: e.target.value ? parseInt(e.target.value, 10) : '' })}
                        className="mt-1 w-full border rounded px-2 py-1.5">
                        <option value="">— Choose contract —</option>
                        {families.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.name} ({CONTRACT_TIER_LABELS[f.license_tier]}{f.current_version ? '' : ', not published'})
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-sm col-span-6 md:col-span-2 flex items-center gap-2 mt-5">
                      <input type="checkbox" checked={row.required}
                        onChange={(e) => updateRow(role, { required: e.target.checked })} />
                      <span className="text-gray-700">Required</span>
                    </label>
                    <label className="text-sm col-span-12 md:col-span-5">
                      <span className="text-gray-700">Notes</span>
                      <input type="text" value={row.notes}
                        onChange={(e) => updateRow(role, { notes: e.target.value })}
                        className="mt-1 w-full border rounded px-2 py-1.5" />
                    </label>
                  </div>
                  <div className="flex justify-end gap-2 mt-3">
                    {assignment && (
                      <Button variant="secondary" onClick={() => remove(role)} disabled={row.removing || row.saving}>
                        {row.removing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                        Remove
                      </Button>
                    )}
                    <Button onClick={() => save(role)} disabled={row.saving || row.removing}>
                      {row.saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      {assignment ? 'Update' : 'Save'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card>
          <CardContent className="p-4">
            <ContractHistory kind="program" id={programId} title="Changes to this programme's contracts" refreshKey={historyKey} />
          </CardContent>
        </Card>
      </div>
    </>
  );
}

export default ProgramContractTemplates;
